import React, { useState, useEffect } from 'react';
import { 
  Search, Download, Upload, Filter, Save, Settings, X, Sparkles, MessageSquare, MessageCircle,
  Loader2, AlertCircle, Check, TrendingUp, Users, Package, FileText, Mail
} from 'lucide-react';
import { websearchService } from '../../services/websearchService';
import { SearchPromptManager } from './SearchPromptEditor';
import { functions } from '../../config/firebase';
import { httpsCallable } from 'firebase/functions';
import { LeadMessenger } from './LeadMessenger';
import { MessageSelector } from './MessageSelector';

export const WebSearchManager = ({ bookId, book, onClose }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [category, setCategory] = useState(book?.websearchSettings?.defaultCategory || 'lead');
  const [location, setLocation] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [savedItems, setSavedItems] = useState([]);
  const [groundingUrls, setGroundingUrls] = useState(book?.websearchSettings?.groundingUrls || []);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [viewMode, setViewMode] = useState('search');
  const [showPromptManager, setShowPromptManager] = useState(false);
  const [showLeadMessenger, setShowLeadMessenger] = useState(false);
  const [showMessageSelector, setShowMessageSelector] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [messageType, setMessageType] = useState(null);
  

  useEffect(() => {
    loadSavedItems();
  }, [bookId, category]);

  const loadSavedItems = async () => {
    try {
      const items = await websearchService.getItems(bookId, { 
        type: category,
        saved: true 
      });
      setSavedItems(items);
    } catch (error) {
      console.error('Error loading saved items:', error);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    setIsSearching(true);
    setError(null);
    setSuccess(null);

    try {
      const searchLeads = httpsCallable(functions, 'searchLeads');
      const response = await searchLeads({
        query: searchQuery,
        category,
        location,
        targetUrls: groundingUrls,
        bookId
      });

      setResults(response.data.results || []);
      setSuccess(`Found ${response.data.results?.length || 0} results`);
      setViewMode('search');
    } catch (error) {
      console.error('Search error:', error);
      setError(error.message || 'Search failed');
    } finally {
      setIsSearching(false);
    }
  };

  const handleSaveItem = async (item) => {
    try {
      await websearchService.saveItem(bookId, {
        ...item,
        saved: true,
        type: category
      });
      await loadSavedItems();
      setSuccess('Item saved successfully');
    } catch (error) {
      console.error('Error saving item:', error);
      setError('Failed to save item');
    }
  };

  const handleSavePrompts = async (prompts) => {
    try {
      await websearchService.updateWebSearchSettings(bookId, {
        searchPrompts: prompts
      });
      setShowPromptManager(false);
    } catch (error) {
      console.error('Error saving prompts:', error);
    }
  };

  const handleExport = async () => {
    const dataToExport = viewMode === 'search' ? results : savedItems;
    const csv = convertToCSV(dataToExport);
    downloadCSV(csv, `${category}_export_${Date.now()}.csv`);
  };

  const convertToCSV = (data) => {
    if (!data.length) return '';
    
    const headers = Object.keys(data[0]).join(',');
    const rows = data.map(item => 
      Object.values(item).map(val => 
        typeof val === 'string' ? `"${val.replace(/"/g, '""')}"` : val
      ).join(',')
    );
    
    return [headers, ...rows].join('\n');
  };

  const downloadCSV = (csv, filename) => {
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-slate-900 w-full max-w-7xl h-screen shadow-2xl flex flex-col">
        {/* Header */}
        <div className="border-b border-slate-800 p-3 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-white">WebSearch Manager</h2>
            <p className="text-slate-400 mt-1">AI-powered lead generation and market research</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Search Form */}
        <div className="border-b border-slate-800 p-3 bg-slate-800/30">
          <form onSubmit={handleSearch} className="space-y-4">
            <div className="flex gap-3">
              <div className="flex-1">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={`Search for ${category}s...`}
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  disabled={isSearching}
                />
              </div>

              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                disabled={isSearching}
              >
                {book?.websearchSettings?.enabledCategories?.map(cat => (
                  <option key={cat} value={cat}>
                    {cat.charAt(0).toUpperCase() + cat.slice(1)}s
                  </option>
                ))}
              </select>

              <button
                type="submit"
                disabled={isSearching || !searchQuery.trim()}
                className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
              >
                {isSearching ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Searching...
                  </>
                ) : (
                  <>
                    <Search className="h-5 w-5" />
                    Search
                  </>
                )}
              </button>
            </div>

            {groundingUrls.length > 0 && (
              <div className="flex items-center gap-2 text-sm text-slate-400">
                <Filter className="h-4 w-4" />
                <span>Searching within: {groundingUrls.join(', ')}</span>
              </div>
            )}
          </form>

          {error && (
            <div className="mt-4 p-3 bg-red-500/10 border border-red-500/50 rounded-lg flex items-center gap-2 text-red-400">
              <AlertCircle className="h-5 w-5" />
              {error}
            </div>
          )}

          {success && (
            <div className="mt-4 p-3 bg-green-500/10 border border-green-500/50 rounded-lg flex items-center gap-2 text-green-400">
              <Check className="h-5 w-5" />
              {success}
            </div>
          )}
        </div>

        {/* View Toggle & Actions */}
        <div className="border-b border-slate-800 p-4 flex items-center justify-between">
          <div className="flex gap-2">
            <button
              onClick={() => setViewMode('search')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                viewMode === 'search'
                  ? 'bg-indigo-600 text-white'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
            >
              Search Results ({results.length})
            </button>
            <button
              onClick={() => setViewMode('saved')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                viewMode === 'saved'
                  ? 'bg-indigo-600 text-white'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
            >
              Saved Items ({savedItems.length})
            </button>
          </div>

          <button
            onClick={handleExport}
            className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg flex items-center gap-2 transition-colors"
          >
            <Download className="h-4 w-4" />
            Export CSV
          </button>

          <button
            onClick={() => setShowPromptManager(true)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
              isDarkMode
                ? 'bg-slate-800 text-slate-200 hover:bg-slate-700'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Sparkles className="h-4 w-4" />
            Edit Prompts
          </button>

          <button
            onClick={() => setShowLeadMessenger(true)}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg flex items-center gap-2 transition-colors"
          >
            <MessageSquare className="h-4 w-4" />
            Message Templates
          </button>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto p-6">
          {isSearching ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-12 w-12 animate-spin text-indigo-500" />
            </div>
          ) : (
            <div className="grid gap-4">
              {(viewMode === 'search' ? results : savedItems).map((item) => (
                <div
                  key={item.id}
                  className="bg-slate-800 w-[900px] mx-auto border border-slate-700 rounded-lg p-4 hover:border-indigo-500 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-white">{item.name || item.title}</h3>
                      {item.company && (
                        <p className="text-slate-400 text-sm">{item.company}</p>
                      )}
                      {item.description && (
                        <p className="text-slate-300 mt-2">{item.description}</p>
                      )}
                      <div className="flex gap-4 items-center mt-3 text-sm">
                        {item.email && (
                          <a href={`mailto:${item.email}`} className="text-indigo-400 hover:text-indigo-300">
                            {item.email}
                          </a>
                        )}
                        {item.phone && (
                          <span className="text-slate-400">{item.phone}</span>
                        )}
                        {item.location && (
                          <span className="text-slate-400">{item.location}</span>
                        )}

                        {viewMode === 'saved' && (
                      <div className="flex gap-2 w-32 mt-0">
                        <button
                          onClick={() => {
                            setSelectedLead(item);
                            setMessageType('whatsapp');
                            setShowMessageSelector(true);
                          }}
                          className="flex-1 px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center justify-center gap-2 transition-colors"
                        >
                          <MessageCircle className="h-4 w-4" />
                          WhatsApp
                        </button>
                        <button
                          onClick={() => {
                            setSelectedLead(item);
                            setMessageType('email');
                            setShowMessageSelector(true);
                          }}
                          className="flex-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center justify-center gap-2 transition-colors"
                        >
                          <Mail className="h-4 w-4" />
                          Email
                        </button>
                      </div>
                    )}
                      </div>
                    </div>

                    {viewMode === 'search' && (
                      <button
                        onClick={() => handleSaveItem(item)}
                        className="ml-4 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg flex items-center gap-2 transition-colors"
                      >
                        <Save className="h-4 w-4" />
                        Save
                      </button>
                    )}
                  </div>
                </div>
              ))}

              {(viewMode === 'search' ? results : savedItems).length === 0 && (
                <div className="text-center py-12 text-slate-400">
                  {viewMode === 'search' 
                    ? 'No search results yet. Try searching above.'
                    : 'No saved items yet. Save some search results to see them here.'}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showPromptManager && (
        <SearchPromptManager
          isOpen={showPromptManager}
          onClose={() => setShowPromptManager(false)}
          bookId={bookId}  
          isDarkMode={true}
        />
      )}

      {showLeadMessenger && (
      <LeadMessenger bookId={bookId} onClose={() => setShowLeadMessenger(false)} />
    )}

    {showMessageSelector && selectedLead && (
      <MessageSelector
        bookId={bookId}
        lead={selectedLead}
        type={messageType}
        onClose={() => {
          setShowMessageSelector(false);
          setSelectedLead(null);
          setMessageType(null);
        }}
      />
    )}
    </div>
  );
};